import { CriteriaResolvers, METADATA_LABEL_KEY } from '../../core/reports/report-query.service';
import {
  ReportDefinition,
  ReportFieldType,
  ReportFilterOperator,
  ReportFilterValue,
  ReportMetadataFilter,
  ReportDynamicFilter,
} from '../../core/models/report.model';
import { VALUELESS_OPERATORS } from './report-builder.model';

/**
 * Read-only, display-ready summary of a {@link ReportDefinition}'s criteria,
 * shown on the results page so it's clear what produced a given set of rows.
 */
export interface ReportCriteriaSummary {
  formLabel: string;
  timeRangeLabel: string;
  filterLines: string[];
  latestPerInstanceLine?: string;
  asOfLine?: string;
  aggregationLabel: string;
  groupByLabel?: string;
}

export interface ReportCriteriaContext {
  formName?: string;
  resolvers: CriteriaResolvers;
  t: (key: string, params?: Record<string, string>) => string;
}

/** Resolves a filter's field to its display label (translated for metadata, literal for dynamic). */
function fieldLabel(
  source: 'metadata' | 'dynamic',
  key: string,
  resolvers: CriteriaResolvers,
  t: (key: string) => string,
): string {
  if (source === 'metadata') return t(METADATA_LABEL_KEY[key] ?? key);
  return resolvers.dynamicFieldLabels.get(key) ?? key;
}

/** Resolves a single (non-array) filter value to its display text. */
function resolveScalar(
  source: 'metadata' | 'dynamic',
  key: string,
  fieldType: ReportFieldType,
  raw: string | number | boolean,
  resolvers: CriteriaResolvers,
  t: (key: string) => string,
): string {
  if (source === 'metadata') {
    if (key === 'checkpoint') return resolvers.checkpointNames.get(String(raw)) ?? String(raw);
    if (key === 'created_by') return resolvers.userNames.get(String(raw)) ?? String(raw);
  }
  if (fieldType === 'enum') {
    const options = resolvers.enumOptions.get(key);
    if (options) return options.get(String(raw)) ?? String(raw);
  }
  if (fieldType === 'boolean') {
    return t(raw ? 'reports.filters.boolean.true' : 'reports.filters.boolean.false');
  }
  return String(raw);
}

/** Formats a filter's stored value (scalar, list, or pair) into display text. */
function resolveValueText(
  source: 'metadata' | 'dynamic',
  key: string,
  fieldType: ReportFieldType,
  operator: ReportFilterOperator,
  value: ReportFilterValue,
  resolvers: CriteriaResolvers,
  t: (key: string, params?: Record<string, string>) => string,
): string {
  const scalar = (v: string | number | boolean) =>
    resolveScalar(source, key, fieldType, v, resolvers, t);

  if (Array.isArray(value)) {
    if (operator === 'between') {
      const [lo, hi] = value;
      return t('reports.results.criteria.between', { low: scalar(lo), high: scalar(hi) });
    }
    return value.map((v) => scalar(v)).join(', ');
  }
  return scalar(value as string | number | boolean);
}

/** Builds one display line for a single metadata or dynamic filter. */
function buildFilterLine(
  source: 'metadata' | 'dynamic',
  key: string,
  filter: ReportMetadataFilter | ReportDynamicFilter,
  resolvers: CriteriaResolvers,
  t: (key: string, params?: Record<string, string>) => string,
): string {
  const field = fieldLabel(source, key, resolvers, t);
  const operator = t('reports.operator.' + filter.operator);

  if (VALUELESS_OPERATORS.includes(filter.operator)) {
    return t('reports.results.criteria.filterLineNoValue', { field, operator });
  }

  const value = resolveValueText(
    source,
    key,
    filter.fieldType,
    filter.operator,
    filter.value,
    resolvers,
    t,
  );
  return t('reports.results.criteria.filterLine', { field, operator, value });
}

function formatTimeRange(def: ReportDefinition, t: (key: string) => string): string {
  const range = def.timeRange;
  if (range.preset !== 'custom') return t('reports.timeRange.' + range.preset);
  if (range.startMs == null || range.endMs == null) return t('reports.timeRange.custom');
  const fmt = (ms: number) => new Date(ms).toLocaleDateString();
  return `${fmt(range.startMs)} – ${fmt(range.endMs)}`;
}

export function buildReportCriteriaSummary(
  def: ReportDefinition,
  ctx: ReportCriteriaContext,
): ReportCriteriaSummary {
  const { resolvers, t } = ctx;

  const filterLines = [
    ...def.metadataFilters.map((f) => buildFilterLine('metadata', f.field, f, resolvers, t)),
    ...def.dynamicFilters.map((f) => buildFilterLine('dynamic', f.fieldKey, f, resolvers, t)),
  ];
  if (filterLines.length === 0) {
    filterLines.push(t('reports.builder.filters.empty'));
  }

  let aggregationLabel = t('reports.agg.' + def.aggregation.mode);
  if (def.aggregation.targetKey && def.aggregation.targetSource) {
    const targetLabel = fieldLabel(def.aggregation.targetSource, def.aggregation.targetKey, resolvers, t);
    aggregationLabel = t('reports.results.criteria.aggregationTarget', {
      aggregation: aggregationLabel,
      field: targetLabel,
    });
  }

  const groupByLabel = def.groupBy
    ? t('reports.results.criteria.groupedBy', {
        field: fieldLabel(def.groupBy.source, def.groupBy.key, resolvers, t),
      })
    : undefined;

  const latestPerInstanceLine = def.latestPerInstance?.enabled
    ? t('reports.results.criteria.latestPerInstanceN', { n: String(def.latestPerInstance.n) })
    : undefined;

  const asOfLine =
    def.asOfTimestampMs != null
      ? `${t('reports.builder.field.asOf')}: ${new Date(def.asOfTimestampMs).toLocaleString()}`
      : undefined;

  return {
    formLabel: ctx.formName ?? '—',
    timeRangeLabel: formatTimeRange(def, t),
    filterLines,
    latestPerInstanceLine,
    asOfLine,
    aggregationLabel,
    groupByLabel,
  };
}
